import { useState, useEffect, useCallback } from 'react';
import { DataTable } from '../components/common/DataTable';
import { PageHeader } from '../components/common/PageHeader';
import { SearchFilter } from '../components/common/SearchFilter';
import { StatusBadge } from '../components/common/StatusBadge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '../components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { MoreHorizontal, Eye, CheckCircle, XCircle, Key, Copy, AlertCircle } from 'lucide-react';
import { formatDateTime } from '../lib/utils';
import { toast } from 'sonner';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

export default function PasswordRequestsPage() {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState('all');
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [showApproveModal, setShowApproveModal] = useState(false);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [showTempPasswordModal, setShowTempPasswordModal] = useState(false);
  const [tempPassword, setTempPassword] = useState('');
  const [adminNotes, setAdminNotes] = useState('');
  const [processing, setProcessing] = useState(false);
  const [stats, setStats] = useState({ pending: 0, approved: 0, rejected: 0 });
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
    totalPages: 0,
  });

  const fetchRequests = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: pagination.page.toString(),
        limit: pagination.limit.toString(),
      });
      if (status !== 'all') params.append('status', status);

      const response = await axios.get(`${API_URL}/api/password-requests?${params}`);
      setRequests(response.data.requests || []);
      setPagination(prev => ({
        ...prev,
        total: response.data.total || 0,
        totalPages: response.data.total_pages || 1,
      }));
    } catch (error) {
      toast.error('Failed to load password requests');
      setRequests([]);
    } finally {
      setLoading(false);
    }
  }, [pagination.page, pagination.limit, status]);

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/password-requests/stats/summary`);
      setStats(response.data);
    } catch {
      // Ignore
    }
  };

  useEffect(() => {
    fetchRequests();
    fetchStats();
  }, [fetchRequests]);

  const handleApprove = async () => {
    if (!selectedRequest) return;
    setProcessing(true);
    try {
      const response = await axios.post(
        `${API_URL}/api/password-requests/${selectedRequest.id}/process`,
        { action: 'approve', admin_notes: adminNotes }
      );
      toast.success('Request approved');
      setTempPassword(response.data.temp_password);
      setShowApproveModal(false);
      setShowTempPasswordModal(true);
      setAdminNotes('');
      fetchRequests();
      fetchStats();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to approve request');
    } finally {
      setProcessing(false);
    }
  };

  const handleReject = async () => {
    if (!selectedRequest) return;
    setProcessing(true);
    try {
      await axios.post(
        `${API_URL}/api/password-requests/${selectedRequest.id}/process`,
        { action: 'reject', admin_notes: adminNotes }
      );
      toast.success('Request rejected');
      setShowRejectModal(false);
      setAdminNotes('');
      setSelectedRequest(null);
      fetchRequests();
      fetchStats();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reject request');
    } finally {
      setProcessing(false);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard');
  };

  const getStatusColor = (s) => {
    const colors = {
      pending: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',
      approved: 'bg-green-500/20 text-green-400 border-green-500/30',
      rejected: 'bg-red-500/20 text-red-400 border-red-500/30',
    };
    return colors[s] || colors.pending;
  };

  const columns = [
    {
      key: 'employee',
      header: 'Employee',
      render: (row) => (
        <div>
          <p className="font-medium text-white">{row.employee_name}</p>
          <p className="text-xs text-slate-500">{row.employee_email}</p>
        </div>
      ),
    },
    {
      key: 'reason',
      header: 'Reason',
      render: (row) => (
        <p className="text-slate-300 text-sm max-w-xs truncate" title={row.reason}>
          {row.reason}
        </p>
      ),
    },
    {
      key: 'status',
      header: 'Status',
      render: (row) => (
        <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium border capitalize ${getStatusColor(row.status)}`}>
          {row.status}
        </span>
      ),
    },
    {
      key: 'requested_at',
      header: 'Requested',
      render: (row) => <span className="text-slate-400 text-sm">{formatDateTime(row.requested_at)}</span>,
    },
    {
      key: 'processed_at',
      header: 'Processed',
      render: (row) => (
        <span className="text-slate-400 text-sm">
          {row.processed_at ? formatDateTime(row.processed_at) : '-'}
        </span>
      ),
    },
    {
      key: 'actions',
      header: '',
      render: (row) => (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="sm" className="h-8 w-8 p-0" data-testid={`request-actions-${row.id}`}>
              <MoreHorizontal className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="bg-slate-900 border-slate-700">
            <DropdownMenuItem 
              onClick={() => setSelectedRequest(row)}
              className="text-slate-300 focus:bg-slate-800 focus:text-white"
            >
              <Eye className="w-4 h-4 mr-2" />
              View Details
            </DropdownMenuItem>
            {row.status === 'pending' && (
              <>
                <DropdownMenuItem 
                  onClick={() => {
                    setSelectedRequest(row);
                    setShowApproveModal(true);
                  }}
                  className="text-green-400 focus:bg-green-500/10 focus:text-green-400"
                >
                  <CheckCircle className="w-4 h-4 mr-2" />
                  Approve
                </DropdownMenuItem>
                <DropdownMenuItem 
                  onClick={() => {
                    setSelectedRequest(row);
                    setShowRejectModal(true);
                  }}
                  className="text-red-400 focus:bg-red-500/10 focus:text-red-400"
                >
                  <XCircle className="w-4 h-4 mr-2" />
                  Reject
                </DropdownMenuItem>
              </>
            )}
            {row.status === 'approved' && row.temp_password && (
              <DropdownMenuItem 
                onClick={() => {
                  setSelectedRequest(row);
                  setTempPassword(row.temp_password);
                  setShowTempPasswordModal(true);
                }}
                className="text-blue-400 focus:bg-blue-500/10 focus:text-blue-400"
              >
                <Key className="w-4 h-4 mr-2" />
                View Temp Password
              </DropdownMenuItem>
            )}
          </DropdownMenuContent>
        </DropdownMenu>
      ),
    },
  ];

  return (
    <div data-testid="password-requests-page">
      <PageHeader
        title="Password Change Requests"
        description="Manage employee password reset requests"
      />

      {/* Stats Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
        <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-sm p-4">
          <p className="text-yellow-400 text-sm font-medium">Pending</p>
          <p className="text-2xl font-bold text-white">{stats.pending}</p>
        </div>
        <div className="bg-green-500/10 border border-green-500/30 rounded-sm p-4">
          <p className="text-green-400 text-sm font-medium">Approved</p>
          <p className="text-2xl font-bold text-white">{stats.approved}</p>
        </div>
        <div className="bg-red-500/10 border border-red-500/30 rounded-sm p-4">
          <p className="text-red-400 text-sm font-medium">Rejected</p>
          <p className="text-2xl font-bold text-white">{stats.rejected}</p>
        </div>
      </div>

      <SearchFilter
        searchValue=""
        onSearchChange={() => {}}
        searchPlaceholder="Search requests..."
        filters={[
          {
            key: 'status',
            placeholder: 'Status',
            value: status,
            onChange: setStatus,
            options: [
              { value: 'all', label: 'All Status' },
              { value: 'pending', label: 'Pending' },
              { value: 'approved', label: 'Approved' },
              { value: 'rejected', label: 'Rejected' },
            ],
          },
        ]}
        onReset={() => setStatus('all')}
      />

      <DataTable
        columns={columns}
        data={requests}
        loading={loading}
        pagination={pagination}
        onPageChange={(page) => setPagination(prev => ({ ...prev, page }))}
        emptyMessage="No password change requests found"
      />

      {/* View Details Modal */}
      <Dialog open={!!selectedRequest && !showApproveModal && !showRejectModal && !showTempPasswordModal} onOpenChange={() => setSelectedRequest(null)}>
        <DialogContent className="bg-slate-900 border-slate-700 text-white">
          <DialogHeader>
            <DialogTitle>Request Details</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-sm text-slate-500">Employee</p>
                <p className="text-white">{selectedRequest?.employee_name}</p>
              </div>
              <div>
                <p className="text-sm text-slate-500">Email</p>
                <p className="text-white">{selectedRequest?.employee_email}</p>
              </div>
              <div>
                <p className="text-sm text-slate-500">Status</p>
                <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium border capitalize ${getStatusColor(selectedRequest?.status)}`}>
                  {selectedRequest?.status}
                </span>
              </div>
              <div>
                <p className="text-sm text-slate-500">Requested At</p>
                <p className="text-white">{formatDateTime(selectedRequest?.requested_at)}</p>
              </div>
            </div>
            <div>
              <p className="text-sm text-slate-500 mb-1">Reason</p>
              <p className="text-slate-300 bg-slate-800 p-3 rounded-sm">{selectedRequest?.reason}</p>
            </div>
            {selectedRequest?.admin_notes && (
              <div>
                <p className="text-sm text-slate-500 mb-1">Admin Notes</p>
                <p className="text-slate-300 bg-slate-800 p-3 rounded-sm">{selectedRequest?.admin_notes}</p>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Approve Modal */}
      <Dialog open={showApproveModal} onOpenChange={setShowApproveModal}>
        <DialogContent className="bg-slate-900 border-slate-700 text-white">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-green-400">
              <CheckCircle className="w-5 h-5" />
              Approve Request
            </DialogTitle>
            <DialogDescription className="text-slate-400">
              This will generate a temporary password for {selectedRequest?.employee_name}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="text-slate-300">Admin Notes (optional)</Label>
              <Textarea
                value={adminNotes}
                onChange={(e) => setAdminNotes(e.target.value)}
                placeholder="Add any notes..."
                className="bg-slate-800 border-slate-700 mt-1"
              />
            </div>
            <div className="flex justify-end gap-3">
              <Button variant="ghost" onClick={() => setShowApproveModal(false)} className="text-slate-400">
                Cancel
              </Button>
              <Button 
                onClick={handleApprove} 
                disabled={processing}
                className="bg-green-500 hover:bg-green-600 text-slate-900"
              >
                {processing ? 'Processing...' : 'Approve & Generate Password'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Reject Modal */}
      <Dialog open={showRejectModal} onOpenChange={setShowRejectModal}>
        <DialogContent className="bg-slate-900 border-slate-700 text-white">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-400">
              <XCircle className="w-5 h-5" />
              Reject Request
            </DialogTitle>
            <DialogDescription className="text-slate-400">
              Reject the password change request from {selectedRequest?.employee_name}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="text-slate-300">Reason for Rejection *</Label>
              <Textarea
                value={adminNotes}
                onChange={(e) => setAdminNotes(e.target.value)}
                placeholder="Provide reason for rejection..."
                className="bg-slate-800 border-slate-700 mt-1"
                required
              />
            </div>
            <div className="flex justify-end gap-3">
              <Button variant="ghost" onClick={() => setShowRejectModal(false)} className="text-slate-400">
                Cancel
              </Button>
              <Button 
                onClick={handleReject} 
                disabled={processing}
                className="bg-red-500 hover:bg-red-600 text-white"
              >
                {processing ? 'Processing...' : 'Reject Request'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Temp Password Modal */}
      <Dialog open={showTempPasswordModal} onOpenChange={setShowTempPasswordModal}>
        <DialogContent className="bg-slate-900 border-slate-700 text-white">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-blue-400">
              <Key className="w-5 h-5" />
              Temporary Password Generated
            </DialogTitle>
            <DialogDescription className="text-slate-400">
              Share this password securely with the employee
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="bg-slate-800 p-4 rounded-sm">
              <p className="text-sm text-slate-500 mb-2">Employee</p>
              <p className="text-white font-medium">{selectedRequest?.employee_name}</p>
              <p className="text-slate-400 text-sm">{selectedRequest?.employee_email}</p>
            </div>
            <div className="bg-green-500/10 border border-green-500/30 p-4 rounded-sm">
              <p className="text-sm text-green-400 mb-2">Temporary Password</p>
              <div className="flex items-center gap-2">
                <code className="text-xl font-mono text-white bg-slate-800 px-3 py-2 rounded flex-1">
                  {tempPassword}
                </code>
                <Button
                  onClick={() => copyToClipboard(tempPassword)}
                  className="bg-slate-700 hover:bg-slate-600"
                >
                  <Copy className="w-4 h-4" />
                </Button>
              </div>
            </div>
            <div className="flex items-start gap-2 text-yellow-400 bg-yellow-500/10 p-3 rounded-sm">
              <AlertCircle className="w-5 h-5 mt-0.5 flex-shrink-0" />
              <p className="text-sm">
                The employee will be required to change this password on their next login.
              </p>
            </div>
            <Button 
              onClick={() => {
                setShowTempPasswordModal(false);
                setSelectedRequest(null);
                setTempPassword('');
              }}
              className="w-full bg-slate-700 hover:bg-slate-600"
            >
              Close
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
